import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { PlayoffService } from '../../../services/playoff.service';
import { Phase } from '../../../models/phase';
import { Tournament } from '../../../models/tournament';
import { TournamentService } from '../../../services/tournament.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { LogService } from '../../../services/log.service';
import _ from 'lodash';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { MatchesComponent } from '../../../components/matches/matches.component';
import { Match } from '../../../models/match';
import { MatchService } from '../../../services/match.service';
import { Team } from '../../../models/team';

@Component({
  selector: 'app-tournament-detail-playoffs',
  imports: [
    FormsModule,
    MatchesComponent,
    SelectModule,
    TranslateModule,
  ],
  templateUrl: './tournament-detail-playoffs.component.html',
  styleUrl: './tournament-detail-playoffs.component.scss'
})
export class TournamentDetailPlayoffsComponent implements OnInit {
  public consolationMatches: Match[] = [];
  private readonly destroy$ = inject(DestroyRef);
  public isFinal: boolean = false;
  public loading: boolean = true;
  public matches: Match[] = [];
  public playoffs: Phase[] = [];
  public positioningMatches: Match[] = [];
  public selectedRound: Phase | undefined;
  public tournament: Tournament | undefined;
  public winner: Team | undefined;

  constructor(
    private cdr: ChangeDetectorRef,
    private logService: LogService,
    private matchService: MatchService,
    private playoffService: PlayoffService,
    private tournamentService: TournamentService,
    private translate: TranslateService,
  ){}

  ngOnInit(): void {
    this.tournament = this.tournamentService.tournament();
    this.getPlayoffs();
  }

  private getPlayoffs(): void {
    if(!this.tournament?.id) return;
    this.playoffService.getTournamentPlayoffs(this.tournament!.id!)
      .pipe(
        takeUntilDestroyed(this.destroy$),
        finalize(() => this.loading = false),
      )
      .subscribe({
        next: (res) => {
          this.logService.debug('[Playoffs] - getTournamentPlayoffs', res);
          this.playoffs = _.orderBy(_.map(_.get(res, 'body.data'), (playoff: Phase) => {
            return {...playoff, name: this.translate.instant(`tournament-phases.${_.toLower(playoff.playoffsStage)}`)}
          }), 'order');
          this.selectedRound = _.first(this.playoffs);
          this.cdr.detectChanges();
          if(this.selectedRound?.id) this.getMatches(this.selectedRound.id);
        },
        error: (err) => {
          this.logService.error('[Playoffs] - getTournamentPlayoffs', err, this.translate.instant('error.playoffs'));
        }
      });
  }

  public getMatches(roundId: string): void {
    this.matches = [];
    this.consolationMatches = [];
    this.positioningMatches = [];
    this.matchService.getMatches(roundId).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
      next: (res: any) => {
        this.logService.debug('[Playoffs] - getMatches', res);
        this.isFinal = _.get(res, 'body.data.playoffsStage') === 'FINAL';
        this.matches = _.get(res, 'body.data.matches', []);
        if(this.isFinal) {
          const finalMatch = _.first(this.matches);
          this.winner = _.find([finalMatch?.whiteTeam, finalMatch?.blackTeam], (team) => team?.id === finalMatch?.winnerTeamId);
        }
        this.consolationMatches = _.get(res, 'body.data.consolation', []);
        this.positioningMatches = _.get(res, 'body.data.positioning', []);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.logService.error('[Playoffs] - getMatches', err, this.translate.instant('error.playoffs-matches'));
      }
    });
  }
  
  public changeRound(): void {
    if(!this.selectedRound?.id) return;
    this.getMatches(this.selectedRound.id);
  }

  public updateTournament(match: Match): void {
    if(!this.tournament?.id) return;
    this.tournamentService.getTournament(this.tournament.id);
    this.winner = _.find([match?.whiteTeam, match?.blackTeam], (team) => team?.id === match?.winnerTeamId);
  }
}
